import type { FieldTypeEnum } from './custom-field.types'

/**
 * Built-in collection blueprints ("Start with" gallery in the collection
 * create flow). Client-side declarative data — the gallery creates a normal
 * collection + scoped custom fields through the existing APIs, so there is no
 * template table and no schema dependency.
 *
 * `category: 'personal'` blueprints surface only in personal spaces (mirrors
 * the project-template convention). A blueprint may declare a reminder rule;
 * the gallery creates it after the fields exist, resolving fields by
 * `fieldName` (see CollectionReminderRule).
 */

export interface CollectionBlueprintField {
  label: string
  fieldName: string
  fieldType: FieldTypeEnum
  /** Passed through to CustomFieldDefinition.settings (e.g. dropdown options). */
  settings?: Record<string, unknown>
}

export interface CollectionBlueprintReminderRule {
  /** DATE_FIELD: remind relative to a DATE field value (one-shot or annual).
   *  DAY_OF_MONTH_FIELD: remind monthly relative to a numeric day-of-month field. */
  mode: 'DATE_FIELD' | 'DAY_OF_MONTH_FIELD'
  /** fieldName of the DATE / day-of-month field this rule reads. */
  fieldName: string
  /** Days before the date to notify (0 = on the day). */
  offsetDays: number[]
  /** Optional BOOLEAN fieldName that suppresses the reminder when true (autopay). */
  skipWhenBooleanFieldName?: string
  recurrence: 'ONE_SHOT' | 'ANNUAL' | 'MONTHLY'
}

export interface CollectionBlueprint {
  key: string
  name: string
  /** Base slug; the create flow uniquifies on collision. */
  slug: string
  icon: string
  description: string
  category: 'personal'
  fields: CollectionBlueprintField[]
  reminderRule?: CollectionBlueprintReminderRule
}

export const COLLECTION_BLUEPRINTS: CollectionBlueprint[] = [
  {
    key: 'bills',
    name: 'Bills',
    slug: 'bills',
    icon: 'receipt',
    description: 'Every bill in one place — with due-date reminders.',
    category: 'personal',
    fields: [
      { label: 'Bank / Card', fieldName: 'bank_or_card', fieldType: 'STRING' },
      { label: 'Account #', fieldName: 'account_number', fieldType: 'STRING' },
      { label: 'Amount', fieldName: 'amount', fieldType: 'CURRENCY' },
      { label: 'Autopay', fieldName: 'autopay', fieldType: 'BOOLEAN' },
      {
        label: 'Due day of month',
        fieldName: 'due_day',
        fieldType: 'NUMBER',
        settings: { min: 1, max: 31 },
      },
      { label: 'Next due', fieldName: 'next_due', fieldType: 'DATE' },
      { label: 'Website', fieldName: 'website', fieldType: 'LINK' },
    ],
    // Monthly reminder from the due day, skipped for autopay bills.
    reminderRule: {
      mode: 'DAY_OF_MONTH_FIELD',
      fieldName: 'due_day',
      offsetDays: [3, 0],
      skipWhenBooleanFieldName: 'autopay',
      recurrence: 'MONTHLY',
    },
  },
  {
    key: 'recipes',
    name: 'Recipes',
    slug: 'recipes',
    icon: 'cooking-pot',
    description: 'Family favorites, by meal — paste a link or add your own.',
    category: 'personal',
    fields: [
      {
        label: 'Meal',
        fieldName: 'meal_type',
        fieldType: 'DROPDOWN',
        settings: {
          options: ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert'],
        },
      },
      { label: 'Link', fieldName: 'source_url', fieldType: 'LINK' },
      { label: 'Photo', fieldName: 'photo', fieldType: 'FILE' },
      { label: 'Favorite', fieldName: 'favorite', fieldType: 'BOOLEAN' },
      {
        label: 'Serves',
        fieldName: 'serves',
        fieldType: 'NUMBER',
      },
    ],
  },
  {
    key: 'trip-ideas',
    name: 'Trip Ideas',
    slug: 'trip-ideas',
    icon: 'airplane-tilt',
    description: 'Places you want to go, before they become plans.',
    category: 'personal',
    fields: [
      { label: 'Link', fieldName: 'link', fieldType: 'LINK' },
      { label: 'Photo', fieldName: 'photo', fieldType: 'FILE' },
      {
        label: 'Best season',
        fieldName: 'season',
        fieldType: 'DROPDOWN',
        settings: { options: ['Spring', 'Summer', 'Fall', 'Winter', 'Any'] },
      },
      { label: 'Been there', fieldName: 'been_there', fieldType: 'BOOLEAN' },
    ],
  },
]
